import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import Layout from '../components/Layout';
import FormPageTitle from '../components/FormPageTitle';
import Pagination from '../components/Pagination';
import { telegramUserService, TelegramUser } from '../services/telegramUserService';
import { authService, User } from '../services/authService';
import { useTranslation } from '../contexts/LocaleContext';
import { ROUTES, APP_CONFIG } from '../constants';

const TelegramUsers: React.FC = () => {
  const t = useTranslation();
  const [users, setUsers] = useState<TelegramUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    loadUsers(currentPage);
  }, [currentPage]);

  const loadCurrentUser = async () => {
    const u = await authService.getCurrentUser();
    setCurrentUser(u);
  };

  const loadUsers = async (page: number = 1) => {
    setLoading(true);
    try {
      const response = await telegramUserService.getList(
        page,
        APP_CONFIG.DEFAULT_PAGE_SIZE
      );
      setUsers(response.data);
      setCurrentPage(page);
      setLastPage(response.last_page);
      setTotalCount(response.total ?? 0);
    } catch (error) {
      console.error('Error loading telegram users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('manage.delete') + '?')) return;
    try {
      await telegramUserService.delete(id);
      loadUsers(currentPage);
    } catch (error) {
      console.error('Error deleting telegram user:', error);
      alert(t('errors.deleteTelegramUser'));
    }
  };

  const handleToggleAccess = async (id: number, currentHasAccess: boolean) => {
    // Optimistic update — flip state instantly, no full reload
    const newAccess = !currentHasAccess;
    setUsers(prev =>
      prev.map(u => u.id === id ? { ...u, hasAccess: newAccess } : u)
    );
    try {
      await telegramUserService.update(id, { hasAccess: newAccess });
    } catch (error) {
      // Revert on failure
      setUsers(prev =>
        prev.map(u => u.id === id ? { ...u, hasAccess: currentHasAccess } : u)
      );
      console.error('Error toggling access:', error);
      alert(t('errors.toggleAccess'));
    }
  };

  const isSuperAdmin = currentUser?.roles?.includes('Admin') ?? false;
  const hasPermission = (permission: string) =>
    currentUser?.permissions?.includes(permission) ?? false;
  const canCreate = hasPermission('telegram-users.create') || isSuperAdmin;
  const canDelete = hasPermission('telegram-users.delete') || isSuperAdmin;
  const canUpdate = hasPermission('telegram-users.update') || isSuperAdmin;

  return (
    <Layout>
      <FormPageTitle title={t('telegramAccess')} />

      {canCreate && (
        <Row className="my-2 mx-0 g-0">
          <Col xs="auto" className="p-0">
            <Link to={ROUTES.TELEGRAM_ACCESS_CREATE}>
              <Button variant="primary" className="text-white">
                {t('giveTelegramAccess')}
              </Button>
            </Link>
          </Col>
        </Row>
      )}

      {loading && (
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      )}
      {!loading && users.length === 0 && (
        <div className="text-secondary text-center my-2">{t('user.notFound')}</div>
      )}

      {!loading &&
        users.map((user) => (
          <Row
            key={user.id}
            className={`p-2 text-bg-light my-2 mx-0 border rounded ${user.hasAccess === false ? 'opacity-75' : ''}`}
          >
            <Col>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h5 className="m-0 d-flex gap-2 flex-column">
                    {user.chatId}
                    {user.hasAccess !== undefined && (
                      <span className="d-flex gap-2 flex-wrap">
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant={user.hasAccess ? 'outline-warning' : 'outline-success'}
                            onClick={() => handleToggleAccess(user.id, user.hasAccess === true)}
                          >
                            {user.hasAccess ? t('category.disable') : t('category.enable')}
                          </Button>
                        )}
                        {user.hasAccess ? (
                          <span className="badge bg-success d-inline-flex align-items-center justify-content-center">
                            {t('access.hasAccess')}
                          </span>
                        ) : (
                          <span className="badge bg-secondary d-inline-flex align-items-center justify-content-center">
                            {t('access.noAccess')}
                          </span>
                        )}
                      </span>
                    )}
                  </h5>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {/* {canUpdate && (
                    <Link
                      to={ROUTES.TELEGRAM_ACCESS_EDIT(user.id)}
                      className="btn btn-outline-primary"
                    >
                      {t('manage.edit')}
                    </Link>
                  )} */}
                  {canDelete && (
                    <Button variant="danger" onClick={() => handleDelete(user.id)}>
                      {t('manage.delete')}
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        ))}

      {!loading && lastPage > 1 && (
        
            <Pagination
              currentPage={currentPage}
              lastPage={lastPage}
              totalCount={totalCount}
              pageSize={APP_CONFIG.DEFAULT_PAGE_SIZE}
              onPageChange={(page) => setCurrentPage(page)}
            />
      )}
    </Layout>
  );
};

export default TelegramUsers;
